
import {Vue, Component, Prop, Watch} from 'vue-property-decorator';
import TreeMapFilterable from '@/components/reports-v3/components/filterables/TreeMapFilterable.vue';
import { TooltipFormatter } from '@/components/reports-v3/components/elements/charts/helpers/tooltipFormatter';
import { DataViewFormatter } from '@/components/reports-v3/components/elements/charts/helpers/dataViewFormatter';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import SelectFilter from '@/components/reports-v3/components/filters/SelectFilter.vue';
import {
    PermissionsGroup,
    ValueText,
} from '@/store/models.def';
import * as echarts from 'echarts';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import settingx from '@/store/modules/settingx';
import { COL, sqlSumByDocType, sqlSum, getTableName } from '@/worker/db/sqlHelpers';

@Component({
    components: {
        TreeMapFilterable,
        SelectFilter,
        IncludeCreditNoteToggle,
        IncludeDebitNoteToggle,
        SlPhModuleOnlyToggle,
    },
})
export default class SharedSalesAnalysisTreeMap extends FilterWidget {
    public get permissionIds(): PermissionsGroup[] {
        return ['sales', 'customers'];
    }

    public get tooltipFormatter() {
        return TooltipFormatter.basicTreeMapWithShare;
    }

    public get dataViewFormatter() {
        return DataViewFormatter.treemapWithShare;
    }

    public filterIds: Array<
        | 'date'
        | 'dateAsOf'
        | 'dateRange'
        | 'stockItems'
        | 'agents'
        | 'customers'
        | 'suppliers'
        | 'docproject'
        | 'dtlproject'
    > = ['dateRange', 'customers', 'docproject', 'dtlproject'];

    public result: any = [];
    public series: echarts.ECharts[] | any = [];

    public selectedGrouping: any = null;

    public get groupingList(): Array<ValueText<string>> {
        const list = [
            {text: 'Stock Item', value: 'stockItems'},
            {text: 'Stock Group', value: 'stockGroup'},
        ];

        return list;
    }

    public get dateFormatted() {
        return this.selectedDateRange;
    }

    public get includeCreditNote() {
        return settingx.now.includeCreditNote;
    }

    public get includeDebitNote() {
        return settingx.now.includeDebitNote;
    }

    public get slPhModuleOnly() {
        return settingx.now.slPhModuleOnly;
    }

    public get expensiveHook() {
        const {
            selectedDateRange,
            selectedCustomers,
            selectedDocProjects,
            selectedDTLProjects,
            selectedGrouping,
            includeCreditNote,
            includeDebitNote,
            slPhModuleOnly,
        } = this;
        return JSON.stringify([
            selectedDateRange,
            selectedCustomers,
            selectedDocProjects,
            selectedDTLProjects,
            selectedGrouping,
            includeCreditNote,
            includeDebitNote,
            slPhModuleOnly,
        ]);
    }

    public async expensiveCalc() {
        this.generateLoadingText(0.2, 0.3);

        // Build filter clauses manually for cross-table UNION
        const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
        const dateFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${this.selectedDateRange[0]} AND ${this.selectedDateRange[1]}`;

        let customerFilter = '';
        if (this.selectedCustomers.length > 0) {
            const list = this.selectedCustomers.map((c) => `'${escapeStr(c)}'`).join(',');
            customerFilter = ` AND ${COL.CODE} IN (${list})`;
        }

        let docProjectFilter = '';
        if (this.selectedDocProjects.length > 0) {
            const list = this.selectedDocProjects.map((p) => `'${escapeStr(p)}'`).join(',');
            docProjectFilter = ` AND "DOCPROJECT" IN (${list})`;
        }

        let dtlProjectFilter = '';
        if (this.selectedDTLProjects.length > 0) {
            const list = this.selectedDTLProjects.map((p) => `'${escapeStr(p)}'`).join(',');
            dtlProjectFilter = ` AND "DTLPROJECT" IN (${list})`;
        }

        const baseFilter = `${dateFilter}${customerFilter}${docProjectFilter}${dtlProjectFilter}`;
        const arBaseFilter = `${dateFilter}${customerFilter}`; // AR doesn't have project filters

        // Get table names
        const salesTable = getTableName('sales');
        const cnTable = getTableName('creditNotes');
        const arTable = getTableName('customerTrades');

        // Get a head for executing queries (ensures all tables are loaded)
        const jobId = this.currentJobId;
        const queryHead = await this._ensureTablesLoaded([
            'sales', 'creditNotes', 'customerTrades',
        ]);
        this.checkInterrupted(jobId);
        this.generateLoadingText(0.3, 0.5);

        // Calculate AR Document amount for AR mode
        let arDocumentAmount = 0;
        if (!this.slPhModuleOnly) {
            // Single UNION query for AR vs SL totals comparison
            const totalsData = await queryHead.rawQuery(`
                WITH ar_totals AS (
                    SELECT
                        SUM(CASE WHEN ${COL.DOCTYPE} = 'IV' THEN TRY_CAST(${COL.LOCALAMOUNT} AS DOUBLE) ELSE 0 END) as iv_amt,
                        SUM(CASE WHEN ${COL.DOCTYPE} = 'DN' THEN TRY_CAST(${COL.LOCALAMOUNT} AS DOUBLE) ELSE 0 END) as dn_amt,
                        SUM(CASE WHEN ${COL.DOCTYPE} = 'CN' THEN TRY_CAST(${COL.LOCALAMOUNT} AS DOUBLE) ELSE 0 END) as cn_amt
                    FROM ${arTable}
                    WHERE ${arBaseFilter}
                ),
                sl_totals AS (
                    SELECT
                        SUM(CASE WHEN docType IN ('IV', 'CS') THEN amount ELSE 0 END) as iv_cs_amt,
                        SUM(CASE WHEN docType = 'DN' THEN amount ELSE 0 END) as dn_amt,
                        SUM(CASE WHEN docType = 'CN' THEN amount ELSE 0 END) as cn_amt
                    FROM (
                        SELECT ${COL.DOCTYPE} as docType, TRY_CAST(${COL.AMOUNT} AS DOUBLE) as amount
                        FROM ${salesTable} WHERE ${baseFilter}
                        UNION ALL
                        SELECT 'CN' as docType, TRY_CAST(${COL.AMOUNT} AS DOUBLE) as amount
                        FROM ${cnTable} WHERE ${baseFilter}
                    ) AS combined
                )
                SELECT
                    (SELECT iv_amt FROM ar_totals) as ar_iv,
                    (SELECT dn_amt FROM ar_totals) as ar_dn,
                    (SELECT cn_amt FROM ar_totals) as ar_cn,
                    (SELECT iv_cs_amt FROM sl_totals) as sl_iv_cs,
                    (SELECT dn_amt FROM sl_totals) as sl_dn,
                    (SELECT cn_amt FROM sl_totals) as sl_cn
            `);

            const row = totalsData[0] || {};
            const arIvAmt = Number(row.ar_iv) || 0;
            const arDnAmt = Number(row.ar_dn) || 0;
            const arCnAmt = Number(row.ar_cn) || 0;
            const slIvCsAmt = Number(row.sl_iv_cs) || 0;
            const slDnAmt = Number(row.sl_dn) || 0;
            const slCnAmt = Number(row.sl_cn) || 0;

            const arAmount = arIvAmt
                + (this.includeDebitNote ? arDnAmt : 0)
                - (this.includeCreditNote ? arCnAmt : 0);
            const slAmount = slIvCsAmt
                + (this.includeDebitNote ? slDnAmt : 0)
                - (this.includeCreditNote ? slCnAmt : 0);

            arDocumentAmount = arAmount - slAmount;
        }
        this.generateLoadingText(0.5, 0.9);

        // Build treemap data using single UNION query
        const colors = [
            '#8975ea',
            '#acbeff',
            '#accdff',
            '#acdaff',
            '#cdc8f9',
            '#c7b5e2',
        ];

        if (this.selectedGrouping === 'stockGroup') {
            // Hierarchical: stockGroup → stockItems - single UNION query
            const data = await queryHead.rawQuery(`
                SELECT stockGroup, itemCode,
                    SUM(CASE WHEN docType IN ('IV', 'CS') THEN amount ELSE 0 END) as iv_cs_amt,
                    SUM(CASE WHEN docType = 'DN' THEN amount ELSE 0 END) as dn_amt,
                    SUM(CASE WHEN docType = 'CN' THEN amount ELSE 0 END) as cn_amt
                FROM (
                    SELECT ${COL.STOCKGROUP} as stockGroup, ${COL.ITEMCODE} as itemCode,
                        ${COL.DOCTYPE} as docType, TRY_CAST(${COL.AMOUNT} AS DOUBLE) as amount
                    FROM ${salesTable} WHERE ${baseFilter}
                    UNION ALL
                    SELECT ${COL.STOCKGROUP} as stockGroup, ${COL.ITEMCODE} as itemCode,
                        'CN' as docType, TRY_CAST(${COL.AMOUNT} AS DOUBLE) as amount
                    FROM ${cnTable} WHERE ${baseFilter}
                ) AS combined
                GROUP BY stockGroup, itemCode
            `);
            this.generateLoadingText(0.95);

            // Build hierarchical structure: stockGroup -> items[]
            const groupMap: Map<string, Map<string, number>> = new Map();
            for (const row of data) {
                const stockGroup = String(row.stockGroup || '');
                const itemCode = String(row.itemCode || '');
                const ivCsAmt = Number(row.iv_cs_amt) || 0;
                const dnAmt = Number(row.dn_amt) || 0;
                const cnAmt = Number(row.cn_amt) || 0;

                const value = ivCsAmt
                    + (this.includeDebitNote ? dnAmt : 0)
                    - (this.includeCreditNote ? cnAmt : 0);

                if (!groupMap.has(stockGroup)) {
                    groupMap.set(stockGroup, new Map());
                }
                groupMap.get(stockGroup)!.set(itemCode, value);
            }

            // Build result array
            this.result = [];
            for (const [stockGroup, items] of groupMap) {
                const children: any[] = [];
                let groupTotal = 0;
                for (const [itemCode, value] of items) {
                    if (value > 0) {
                        children.push({
                            name: itemCode,
                            value,
                            color: colors,
                            colorSaturation: [0.9, 0.4],
                        });
                        groupTotal += value;
                    }
                }
                if (groupTotal > 0) {
                    this.result.push({
                        name: stockGroup,
                        value: groupTotal,
                        color: colors,
                        colorSaturation: [0.9, 0.4],
                        children,
                    });
                }
            }
        } else {
            // Flat: stockItems only - single UNION query
            const data = await queryHead.rawQuery(`
                SELECT itemCode,
                    SUM(CASE WHEN docType IN ('IV', 'CS') THEN amount ELSE 0 END) as iv_cs_amt,
                    SUM(CASE WHEN docType = 'DN' THEN amount ELSE 0 END) as dn_amt,
                    SUM(CASE WHEN docType = 'CN' THEN amount ELSE 0 END) as cn_amt
                FROM (
                    SELECT ${COL.ITEMCODE} as itemCode, ${COL.DOCTYPE} as docType,
                        TRY_CAST(${COL.AMOUNT} AS DOUBLE) as amount
                    FROM ${salesTable} WHERE ${baseFilter}
                    UNION ALL
                    SELECT ${COL.ITEMCODE} as itemCode, 'CN' as docType,
                        TRY_CAST(${COL.AMOUNT} AS DOUBLE) as amount
                    FROM ${cnTable} WHERE ${baseFilter}
                ) AS combined
                GROUP BY itemCode
            `);
            this.generateLoadingText(0.95);

            // Build flat result
            this.result = [];
            for (const row of data) {
                const itemCode = String(row.itemCode || '');
                const ivCsAmt = Number(row.iv_cs_amt) || 0;
                const dnAmt = Number(row.dn_amt) || 0;
                const cnAmt = Number(row.cn_amt) || 0;

                const value = ivCsAmt
                    + (this.includeDebitNote ? dnAmt : 0)
                    - (this.includeCreditNote ? cnAmt : 0);

                if (value > 0) {
                    this.result.push({
                        name: itemCode,
                        value,
                        color: colors,
                        colorSaturation: [0.9, 0.4],
                        children: [],
                    });
                }
            }
        }

        // Add AR Document group for AR mode
        if (!this.slPhModuleOnly && arDocumentAmount > 0) {
            this.result.push({
                name: 'Customer Module Documents',
                value: arDocumentAmount,
                color: [
                    '#f5a623',
                    '#f8c471',
                    '#fad7a0',
                    '#fdebd0',
                    '#fef9e7',
                    '#fff5eb',
                ],
                colorSaturation: [0.9, 0.4],
                children: [],
            });
        }

        this.generateLoadingText(1);

        this.series = [];

        const stock = {
            name: 'Sales Analysis',
            type: 'treemap',
            roam: 'move',
            width: '100%',
            // drillDownIcon: '👆🏻',
            drillDownIcon: '',
            // leafDepth: 1,
            label: {
                color: '#000',
            },
            itemStyle: {
                gapWidth: 1,
                borderWidth: 1,
            },
            levels: [
                {
                    itemStyle: {
                        // borderColor: '#555',
                        borderWidth: 2,
                        gapWidth: 2,
                    },
                    upperLabel: {
                        show: false,
                        fontWeight: 'bold',
                        color: '#000',
                    },
                    emphasis: {
                        itemStyle: {
                            borderWidth: 0,
                            gapWidth: 2,
                        },
                        upperLabel: {
                            show: true,
                            fontWeight: 'bold',
                            color: '#000',
                        },
                    },
                },
                {
                    colorSaturation: [0.3, 0.6],
                    itemStyle: {
                        borderColorSaturation: 0.7,
                        gapWidth: 2,
                        borderWidth: 2,
                    },
                    upperLabel: {
                        show: true,
                        color: '#000',
                        fontWeight: 'bold',
                    },
                    emphasis: {
                        itemStyle: {
                            borderWidth: 0,
                            gapWidth: 1,
                        },
                        upperLabel: {
                            show: true,
                            color: '#000',
                            fontWeight: 'bold',
                        },
                    },
                },
                {
                    itemStyle: {
                        // borderColor: '#555',
                        gapWidth: 1,
                        borderWidth: 1,
                    },
                    upperLabel: {
                        show: true,
                        color: '#000',
                        fontWeight: 'bold',
                    },
                    emphasis: {
                        itemStyle: {
                            borderWidth: 0,
                            gapWidth: 1,
                        },
                        upperLabel: {
                            show: true,
                            color: '#000',
                            fontWeight: 'bold',
                        },
                    },
                },
            ],
            data: this.result,
        };

        this.series = [stock];

        this.saveHistory('result', 'series');
    }

    public onHovering(value: boolean, filterId: string) {
        this.$emit('hovering', value, filterId);
    }

    public onEditing(value: boolean, filterId: string) {
        this.$emit('editing', value, filterId);
    }
}
